import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../App";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { 
  Users, 
  MessageSquare, 
  Mic, 
  Volume2, 
  Calendar, 
  CreditCard, 
  AlertCircle,
  MessageCircle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  User,
  Phone,
  LogOut,
  Plus,
  Activity,
  Mail,
  Edit2,
  Trash2,
  ToggleLeft,
  ToggleRight,
  X,
  Check,
  Globe
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "../../api/axios";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_interpret-now/artifacts/8sijz41w_PassportTalk.png";

const AdminDashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Existing state
  const [agents, setAgents] = useState([]);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddAgent, setShowAddAgent] = useState(false);
  const [newAgent, setNewAgent] = useState({
    user_id: "",
    display_name: "",
    whatsapp_number: "",
    specialties: ""
  });
  
  // Edit/Delete agent state
  const [editingAgent, setEditingAgent] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    bio: "",
    phone_number: "",
    languages: "",
    expertise_tags: "",
    availability_status: "available",
    whatsapp_enabled: true
  });
  
  // New ops dashboard state
  const [metrics, setMetrics] = useState(null);
  const [errors, setErrors] = useState([]);
  const [users, setUsers] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [errorFilter, setErrorFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error("Admin access required");
      navigate('/');
      return;
    }
    fetchAllData();
  }, [user, navigate]);

  const fetchAllData = async () => {
    try {
      setRefreshing(true);
      
      // Fetch existing admin data and live agents
      const [agentsRes, callsRes, liveAgentsRes] = await Promise.all([
        api.get('/admin/agents'),
        api.get('/admin/calls'),
        api.get('/live-agents')
      ]);
      // Combine admin agents and live agents, avoiding duplicates
      const adminAgents = agentsRes.data || [];
      const liveAgents = liveAgentsRes.data || [];
      const combinedAgents = [...adminAgents, ...liveAgents.filter(la => 
        !adminAgents.some(aa => aa.id === la.id)
      )];
      setAgents(combinedAgents);
      setCalls(callsRes.data);
      
      // Fetch ops dashboard data
      try {
        const dashboardRes = await api.get('/admin/dashboard');
        setMetrics(dashboardRes.data);
        setErrors(dashboardRes.data.recent_errors || []);
      } catch (e) {
        console.log('Dashboard metrics not available yet');
      }
      
      try {
        const usersRes = await api.get('/admin/users?limit=50');
        setUsers(usersRes.data.users || []);
      } catch (e) {
        console.log('Users endpoint not available');
      }
      
      try {
        const feedbackRes = await api.get('/admin/feedback?limit=50');
        setFeedback(feedbackRes.data.feedback || []);
      } catch (e) {
        console.log('Feedback endpoint not available');
      }
      
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      const res = await api.get(`/admin/user/${userId}`);
      setUserDetails(res.data);
      setSelectedUser(userId);
    } catch (err) {
      toast.error('Failed to fetch user details');
    }
  };

  const resendFeedbackEmail = async (feedbackId) => {
    try {
      const res = await api.post(`/admin/resend-feedback-email/${feedbackId}`);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    }
  };

  const handleAddAgent = async () => {
    try {
      const specialtiesArray = newAgent.specialties.split(",").map(s => s.trim());
      await api.post('/admin/agents', {
        ...newAgent,
        specialties: specialtiesArray
      });
      toast.success("Agent created!");
      setShowAddAgent(false);
      fetchAllData();
      setNewAgent({ user_id: "", display_name: "", whatsapp_number: "", specialties: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create agent");
    }
  };

  // Edit agent - open modal with agent data
  const handleEditAgent = (agent) => {
    setEditingAgent(agent);
    setEditForm({
      name: agent.name || agent.display_name || "",
      bio: agent.bio || "",
      phone_number: agent.phone_number || agent.whatsapp_number || "",
      languages: (agent.languages || []).join(", "),
      expertise_tags: (agent.expertise_tags || agent.specialties || []).join(", "),
      availability_status: agent.availability_status || "available",
      whatsapp_enabled: agent.whatsapp_enabled !== false
    });
    setShowEditModal(true);
  };

  // Save agent changes
  const handleSaveAgent = async () => {
    if (!editingAgent) return;
    
    try {
      const languagesArray = editForm.languages.split(",").map(s => s.trim()).filter(Boolean);
      const tagsArray = editForm.expertise_tags.split(",").map(s => s.trim()).filter(Boolean);
      
      await api.put(`/live-agents/${editingAgent.id}`, {
        name: editForm.name,
        bio: editForm.bio,
        phone_number: editForm.phone_number,
        languages: languagesArray,
        expertise_tags: tagsArray,
        availability_status: editForm.availability_status,
        whatsapp_enabled: editForm.whatsapp_enabled
      });
      
      toast.success("Agent updated successfully!");
      setShowEditModal(false);
      setEditingAgent(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update agent");
    }
  };

  // Delete agent
  const handleDeleteAgent = async (agentId) => {
    try {
      await api.delete(`/live-agents/${agentId}`);
      toast.success("Agent deleted successfully!");
      setShowDeleteConfirm(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete agent");
    }
  };

  // Toggle agent availability
  const handleToggleAvailability = async (agent) => {
    const newStatus = agent.availability_status === "available" ? "busy" : "available";
    
    try {
      await api.patch(`/live-agents/${agent.id}/status`, {
        availability_status: newStatus
      });
      toast.success(`Agent is now ${newStatus}`);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "N/A";
    const mins = Math.floor(seconds / 60);
    return `${mins} min`;
  };

  const filteredErrors = errorFilter 
    ? errors.filter(e => e.error_type === errorFilter)
    : errors;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="PassportTalk" className="h-10" />
            <div>
              <h1 className="text-xl font-bold">Admin Dashboard</h1>
              <p className="text-slate-400 text-sm">Operations & Monitoring</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={fetchAllData}
              disabled={refreshing}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 px-4 py-2 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-2 mt-4 flex-wrap">
          {['overview', 'errors', 'users', 'feedback', 'agents', 'calls'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg transition-colors capitalize ${
                activeTab === tab 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </header>

      <main className="p-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Metrics Grid */}
            {metrics ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <MetricCard 
                  icon={<Users className="w-6 h-6" />}
                  label="Total Users"
                  value={metrics.total_users}
                  subtext={`+${metrics.new_users_24h} (24h) / +${metrics.new_users_7d} (7d)`}
                  color="blue"
                />
                <MetricCard 
                  icon={<MessageSquare className="w-6 h-6" />}
                  label="Translations"
                  value={metrics.translations_total}
                  subtext={`+${metrics.translations_24h} (24h)`}
                  color="green"
                />
                <MetricCard 
                  icon={<Mic className="w-6 h-6" />}
                  label="STT Used"
                  value={metrics.stt_count}
                  color="purple"
                />
                <MetricCard 
                  icon={<Volume2 className="w-6 h-6" />}
                  label="TTS Used"
                  value={metrics.tts_count}
                  color="indigo"
                />
                <MetricCard 
                  icon={<Calendar className="w-6 h-6" />}
                  label="Bookings Created"
                  value={metrics.bookings_created}
                  color="cyan"
                />
                <MetricCard 
                  icon={<CreditCard className="w-6 h-6" />}
                  label="Bookings Paid"
                  value={metrics.bookings_paid}
                  color="emerald"
                />
                <MetricCard 
                  icon={<AlertCircle className="w-6 h-6" />}
                  label="Failed Payments"
                  value={metrics.failed_payments}
                  color="red"
                />
                <MetricCard 
                  icon={<MessageCircle className="w-6 h-6" />}
                  label="Feedback"
                  value={metrics.feedback_count}
                  color="amber"
                />
              </div>
            ) : (
              <div className="bg-slate-800 rounded-xl p-8 text-center">
                <Activity className="w-12 h-12 mx-auto mb-4 text-slate-500" />
                <p className="text-slate-400">Metrics loading...</p>
              </div>
            )}

            {/* Recent Errors Preview */}
            <div className="bg-slate-800 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Recent Errors</h2>
                <button 
                  onClick={() => setActiveTab('errors')}
                  className="text-blue-400 hover:text-blue-300 text-sm"
                >
                  View All →
                </button>
              </div>
              {errors.length === 0 ? (
                <p className="text-slate-400">No recent errors 🎉</p>
              ) : (
                <div className="space-y-2">
                  {errors.slice(0, 5).map((error, idx) => (
                    <ErrorRow key={idx} error={error} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Errors Tab */}
        {activeTab === 'errors' && (
          <div className="space-y-4">
            {/* Error Filter */}
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setErrorFilter('')}
                className={`px-3 py-1 rounded-full text-sm ${!errorFilter ? 'bg-blue-600' : 'bg-slate-700'}`}
              >
                All
              </button>
              {['stt', 'tts', 'stripe', 'booking', 'auth', 'translation'].map(type => (
                <button
                  key={type}
                  onClick={() => setErrorFilter(type)}
                  className={`px-3 py-1 rounded-full text-sm uppercase ${errorFilter === type ? 'bg-blue-600' : 'bg-slate-700'}`}
                >
                  {type}
                </button>
              ))}
            </div>

            {/* Error List */}
            <div className="bg-slate-800 rounded-xl p-4">
              {filteredErrors.length === 0 ? (
                <p className="text-slate-400 text-center py-8">No errors found</p>
              ) : (
                <div className="space-y-2">
                  {filteredErrors.map((error, idx) => (
                    <ErrorRow key={idx} error={error} expanded />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* User List */}
            <div className="bg-slate-800 rounded-xl p-4 max-h-[70vh] overflow-y-auto">
              <h2 className="text-lg font-semibold mb-4 sticky top-0 bg-slate-800 py-2">
                Users ({users.length})
              </h2>
              <div className="space-y-2">
                {users.map(u => (
                  <div
                    key={u.id}
                    onClick={() => fetchUserDetails(u.id)}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedUser === u.id 
                        ? 'bg-blue-600' 
                        : 'bg-slate-700 hover:bg-slate-600'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{u.name || 'Unknown'}</p>
                        <p className="text-sm text-slate-400">{u.email}</p>
                      </div>
                      <div className="text-right text-sm">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          u.platform === 'ios' ? 'bg-gray-600' :
                          u.platform === 'android' ? 'bg-green-600' :
                          'bg-slate-600'
                        }`}>
                          {u.platform}
                        </span>
                        {u.last_error && (
                          <AlertCircle className="w-4 h-4 text-red-400 inline ml-2" />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* User Details */}
            <div className="bg-slate-800 rounded-xl p-4">
              {userDetails ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">{userDetails.user.name}</h3>
                      <p className="text-slate-400 text-sm">{userDetails.user.email}</p>
                    </div>
                  </div>

                  {/* Session Info */}
                  <div className="bg-slate-700 rounded-lg p-4">
                    <h4 className="font-medium mb-2">Session Diagnostics</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-slate-400">Platform:</span>
                        <span className="ml-2">{userDetails.session.platform}</span>
                      </div>
                      <div>
                        <span className="text-slate-400">Browser:</span>
                        <span className="ml-2">{userDetails.session.browser}</span>
                      </div>
                      <div>
                        <span className="text-slate-400">Language:</span>
                        <span className="ml-2">{userDetails.session.language}</span>
                      </div>
                      <div>
                        <span className="text-slate-400">Voice Tried:</span>
                        <span className="ml-2">{userDetails.session.voice_attempted ? '✅' : '❌'}</span>
                      </div>
                      <div>
                        <span className="text-slate-400">STT Used:</span>
                        <span className="ml-2">{userDetails.session.stt_used ? '✅' : '❌'}</span>
                      </div>
                      <div>
                        <span className="text-slate-400">TTS Used:</span>
                        <span className="ml-2">{userDetails.session.tts_used ? '✅' : '❌'}</span>
                      </div>
                    </div>
                    {userDetails.session.last_error && (
                      <div className="mt-3 p-2 bg-red-900/30 rounded text-red-300 text-sm">
                        <strong>Last Error:</strong> {userDetails.session.last_error}
                      </div>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-4 gap-2">
                    <div className="bg-slate-700 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold">{userDetails.stats.translations_count}</p>
                      <p className="text-xs text-slate-400">Translations</p>
                    </div>
                    <div className="bg-slate-700 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold">{userDetails.stats.bookings_count}</p>
                      <p className="text-xs text-slate-400">Bookings</p>
                    </div>
                    <div className="bg-slate-700 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold">{userDetails.stats.errors_count}</p>
                      <p className="text-xs text-slate-400">Errors</p>
                    </div>
                    <div className="bg-slate-700 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold">{userDetails.stats.feedback_count}</p>
                      <p className="text-xs text-slate-400">Feedback</p>
                    </div>
                  </div>

                  {/* Recent Errors for this user */}
                  {userDetails.recent_errors.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2">Recent Errors</h4>
                      <div className="space-y-1">
                        {userDetails.recent_errors.map((err, idx) => (
                          <div key={idx} className="bg-slate-700 rounded p-2 text-sm">
                            <span className="text-red-400">[{err.error_type}]</span> {err.message}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 text-slate-400">
                  Select a user to view details
                </div>
              )}
            </div>
          </div>
        )}

        {/* Feedback Tab */}
        {activeTab === 'feedback' && (
          <div className="bg-slate-800 rounded-xl p-4">
            <h2 className="text-lg font-semibold mb-4">User Feedback ({feedback.length})</h2>
            {feedback.length === 0 ? (
              <p className="text-slate-400 text-center py-8">No feedback yet</p>
            ) : (
              <div className="space-y-3">
                {feedback.map((fb, idx) => (
                  <div key={idx} className="bg-slate-700 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium">{fb.user_email || 'Anonymous'}</span>
                          <span className="text-xs bg-slate-600 px-2 py-0.5 rounded">
                            {fb.app_language?.toUpperCase()}
                          </span>
                          <span className="text-xs text-slate-400">
                            {fb.timestamp ? new Date(fb.timestamp).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                        <p className="text-slate-300">{fb.message}</p>
                      </div>
                      <button
                        onClick={() => resendFeedbackEmail(fb.id)}
                        className="ml-4 px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-sm flex items-center gap-1"
                        title="Send email notification"
                      >
                        <Mail className="w-4 h-4" />
                        Send
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Agents Tab (existing) */}
        {activeTab === 'agents' && (
          <div className="bg-slate-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold">Language Agents ({agents.length})</h2>
              <Dialog open={showAddAgent} onOpenChange={setShowAddAgent}>
                <DialogTrigger asChild>
                  <Button className="flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Add Agent
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-slate-800 border-slate-700">
                  <DialogHeader>
                    <DialogTitle className="text-white">Create New Agent</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="user_id" className="text-slate-300">User ID</Label>
                      <Input
                        id="user_id"
                        value={newAgent.user_id}
                        onChange={(e) => setNewAgent({...newAgent, user_id: e.target.value})}
                        className="bg-slate-700 border-slate-600 text-white"
                        placeholder="User ID (UUID)"
                      />
                    </div>
                    <div>
                      <Label htmlFor="display_name" className="text-slate-300">Display Name</Label>
                      <Input
                        id="display_name"
                        value={newAgent.display_name}
                        onChange={(e) => setNewAgent({...newAgent, display_name: e.target.value})}
                        className="bg-slate-700 border-slate-600 text-white"
                        placeholder="Agent display name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="whatsapp_number" className="text-slate-300">WhatsApp Number</Label>
                      <Input
                        id="whatsapp_number"
                        value={newAgent.whatsapp_number}
                        onChange={(e) => setNewAgent({...newAgent, whatsapp_number: e.target.value})}
                        className="bg-slate-700 border-slate-600 text-white"
                        placeholder="+1234567890"
                      />
                    </div>
                    <div>
                      <Label htmlFor="specialties" className="text-slate-300">Specialties (comma separated)</Label>
                      <Input
                        id="specialties"
                        value={newAgent.specialties}
                        onChange={(e) => setNewAgent({...newAgent, specialties: e.target.value})}
                        className="bg-slate-700 border-slate-600 text-white"
                        placeholder="Travel, Legal, Medical"
                      />
                    </div>
                    <Button onClick={handleAddAgent} className="w-full">Create Agent</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            
            {agents.length === 0 ? (
              <p className="text-slate-400 text-center py-8">No agents registered yet</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {agents.map(agent => (
                  <div key={agent.id} className="bg-slate-700 rounded-lg p-4 group relative hover:bg-slate-600/70 transition-colors">
                    {/* Status indicator */}
                    <div className={`absolute top-3 right-3 w-3 h-3 rounded-full ${
                      agent.availability_status === 'available' ? 'bg-green-500' :
                      agent.availability_status === 'busy' ? 'bg-amber-500' : 'bg-slate-500'
                    }`} title={agent.availability_status || 'unknown'} />
                    
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-6 h-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{agent.name || agent.display_name}</p>
                        <p className="text-sm text-slate-400 truncate">{agent.bio || 'Language expert'}</p>
                        
                        {/* Languages */}
                        {(agent.languages && agent.languages.length > 0) && (
                          <div className="flex items-center gap-1 mt-1 text-xs text-slate-400">
                            <Globe className="w-3 h-3" />
                            {agent.languages.join(", ").toUpperCase()}
                          </div>
                        )}
                        
                        {/* Phone number */}
                        {agent.phone_number && (
                          <div className="flex items-center gap-1 mt-1 text-xs text-slate-400">
                            <Phone className="w-3 h-3" />
                            {agent.phone_number}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Specialties / Expertise */}
                    {(agent.expertise_tags || agent.specialties)?.length > 0 && (
                      <div className="flex gap-1 mt-3 flex-wrap">
                        {(agent.expertise_tags || agent.specialties).map((s, i) => (
                          <span key={i} className="text-xs bg-slate-600 px-2 py-0.5 rounded">{s}</span>
                        ))}
                      </div>
                    )}
                    
                    {/* Action buttons - visible on hover */}
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-600">
                      {/* Availability Toggle */}
                      <button
                        onClick={() => handleToggleAvailability(agent)}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          agent.availability_status === 'available' 
                            ? 'bg-green-600/20 text-green-400 hover:bg-green-600/30'
                            : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                        }`}
                      >
                        {agent.availability_status === 'available' ? (
                          <><ToggleRight className="w-4 h-4" /> Available</>
                        ) : (
                          <><ToggleLeft className="w-4 h-4" /> {agent.availability_status || 'Offline'}</>
                        )}
                      </button>
                      
                      {/* Edit & Delete */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditAgent(agent)}
                          className="p-2 bg-slate-600 hover:bg-blue-600 rounded-lg transition-colors"
                          title="Edit Agent"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(agent.id)}
                          className="p-2 bg-slate-600 hover:bg-red-600 rounded-lg transition-colors"
                          title="Delete Agent"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Delete Confirmation */}
                    {showDeleteConfirm === agent.id && (
                      <div className="absolute inset-0 bg-slate-800/95 rounded-lg flex flex-col items-center justify-center p-4 z-10">
                        <p className="text-center mb-4">Delete <strong>{agent.name || agent.display_name}</strong>?</p>
                        <p className="text-sm text-slate-400 text-center mb-4">This cannot be undone.</p>
                        <div className="flex gap-3">
                          <button
                            onClick={() => setShowDeleteConfirm(null)}
                            className="px-4 py-2 bg-slate-600 hover:bg-slate-500 rounded-lg flex items-center gap-2"
                          >
                            <X className="w-4 h-4" /> Cancel
                          </button>
                          <button
                            onClick={() => handleDeleteAgent(agent.id)}
                            className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg flex items-center gap-2"
                          >
                            <Trash2 className="w-4 h-4" /> Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {/* Edit Agent Modal */}
            <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
              <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-white">Edit Agent</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-300">Name</Label>
                    <Input
                      value={editForm.name}
                      onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Bio</Label>
                    <Input
                      value={editForm.bio}
                      onChange={(e) => setEditForm({...editForm, bio: e.target.value})}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="Language expert..."
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">WhatsApp Number (E.164 format)</Label>
                    <Input
                      value={editForm.phone_number}
                      onChange={(e) => setEditForm({...editForm, phone_number: e.target.value})}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="+1234567890"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Languages (comma separated)</Label>
                    <Input
                      value={editForm.languages}
                      onChange={(e) => setEditForm({...editForm, languages: e.target.value})}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="en, es, fr"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Categories / Specialties</Label>
                    <Input
                      value={editForm.expertise_tags}
                      onChange={(e) => setEditForm({...editForm, expertise_tags: e.target.value})}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="Medical, Business, Travel"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Status</Label>
                    <select
                      value={editForm.availability_status}
                      onChange={(e) => setEditForm({...editForm, availability_status: e.target.value})}
                      className="w-full bg-slate-700 border border-slate-600 text-white rounded-md px-3 py-2"
                    >
                      <option value="available">Available</option>
                      <option value="busy">Busy</option>
                      <option value="offline">Offline</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="whatsapp_enabled"
                      checked={editForm.whatsapp_enabled}
                      onChange={(e) => setEditForm({...editForm, whatsapp_enabled: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="whatsapp_enabled" className="text-slate-300">WhatsApp Enabled</Label>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button 
                      variant="outline" 
                      onClick={() => setShowEditModal(false)}
                      className="flex-1 bg-slate-700 border-slate-600 text-white hover:bg-slate-600"
                    >
                      Cancel
                    </Button>
                    <Button onClick={handleSaveAgent} className="flex-1 flex items-center justify-center gap-2">
                      <Check className="w-4 h-4" /> Save Changes
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Calls Tab (existing) */}
        {activeTab === 'calls' && (
          <div className="bg-slate-800 rounded-xl p-6">
            <h2 className="text-lg font-semibold mb-4">Call History ({calls.length})</h2>
            {calls.length === 0 ? (
              <p className="text-slate-400 text-center py-8">No calls yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-slate-400 border-b border-slate-700">
                      <th className="pb-2">Traveler</th>
                      <th className="pb-2">Agent</th>
                      <th className="pb-2">Date</th>
                      <th className="pb-2">Duration</th>
                      <th className="pb-2">Rating</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calls.map(call => (
                      <tr key={call.id} className="border-b border-slate-700/50">
                        <td className="py-3">{call.traveler_name}</td>
                        <td className="py-3">{call.agent_name}</td>
                        <td className="py-3 text-slate-400">{formatDate(call.start_time)}</td>
                        <td className="py-3">{formatDuration(call.duration_seconds)}</td>
                        <td className="py-3">{call.rating ? `⭐ ${call.rating}` : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ icon, label, value, subtext, color }) => {
  const colorClasses = {
    blue: 'bg-blue-900/30 border-blue-700 text-blue-400',
    green: 'bg-green-900/30 border-green-700 text-green-400',
    purple: 'bg-purple-900/30 border-purple-700 text-purple-400',
    indigo: 'bg-indigo-900/30 border-indigo-700 text-indigo-400',
    cyan: 'bg-cyan-900/30 border-cyan-700 text-cyan-400',
    emerald: 'bg-emerald-900/30 border-emerald-700 text-emerald-400',
    red: 'bg-red-900/30 border-red-700 text-red-400',
    amber: 'bg-amber-900/30 border-amber-700 text-amber-400',
  };

  return (
    <div className={`rounded-xl p-4 border ${colorClasses[color]}`}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="text-sm text-slate-300">{label}</span>
      </div>
      <p className="text-3xl font-bold text-white">{value ?? 0}</p>
      {subtext && <p className="text-xs text-slate-400 mt-1">{subtext}</p>}
    </div>
  );
};

// Error Row Component
const ErrorRow = ({ error, expanded = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const typeColors = {
    stt: 'bg-purple-600',
    tts: 'bg-indigo-600',
    stripe: 'bg-yellow-600',
    booking: 'bg-cyan-600',
    auth: 'bg-red-600',
    translation: 'bg-green-600',
    ui: 'bg-gray-600'
  };

  return (
    <div className="bg-slate-700 rounded-lg p-3">
      <div 
        className="flex items-start justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-start gap-2 flex-1">
          <span className={`px-2 py-0.5 rounded text-xs uppercase ${typeColors[error.error_type] || 'bg-gray-600'}`}>
            {error.error_type}
          </span>
          <div className="flex-1">
            <p className="text-sm">{error.message}</p>
            <div className="flex gap-2 mt-1 text-xs text-slate-400">
              {error.platform && <span>📱 {error.platform}</span>}
              {error.user_email && <span>👤 {error.user_email}</span>}
              <span>🕐 {new Date(error.timestamp).toLocaleString()}</span>
            </div>
          </div>
        </div>
        {expanded && (
          isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
        )}
      </div>
      
      {isExpanded && error.stack_trace && (
        <pre className="mt-2 p-2 bg-slate-800 rounded text-xs overflow-x-auto">
          {error.stack_trace}
        </pre>
      )}
    </div>
  );
};

export default AdminDashboard;
